import { useEffect, useState } from "react";
import { Scale, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";
import type { Zelt } from "@/data/zelte";

interface VergleichBarProps {
  ids: string[];
  currentPath: string;
  onRemove: (id: string) => void;
  onClear: () => void;
  onNavigate: (path: string) => void;
}

export function VergleichBar({ ids, currentPath, onRemove, onClear, onNavigate }: VergleichBarProps) {
  const [zelte, setZelte] = useState<Zelt[]>([]);

  useEffect(() => {
    if (ids.length === 0) {
      setZelte([]);
      return;
    }

    const controller = new AbortController();
    fetch("/api/vergleich", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
      signal: controller.signal,
    })
      .then(res => res.json())
      .then(data => setZelte(data.zelte || []))
      .catch(() => {});

    return () => controller.abort();
  }, [ids]);

  if (ids.length === 0 || currentPath === "/vergleich") return null;

  return (
    <div
      className={cn(
        "fixed bottom-0 inset-x-0 z-40 border-t bg-background/95 backdrop-blur-md",
        "shadow-[0_-8px_30px_-12px_rgba(0,0,0,0.25)]"
      )}
      role="region"
      aria-label="Zelte im Vergleich"
    >
      <Container>
        <div className="flex items-center gap-3 py-3">
          <div className="hidden sm:flex items-center gap-2 text-sm font-semibold text-primary flex-shrink-0">
            <Scale className="h-4 w-4" />
            Vergleich
          </div>

          <div className="flex-1 flex items-center gap-2 overflow-x-auto min-w-0">
            {zelte.map(zelt => (
              <div
                key={zelt.id}
                className="flex items-center gap-2 rounded-lg border bg-card pl-1 pr-1 py-1 flex-shrink-0"
              >
                <img
                  src={zelt.bilder[0]}
                  alt=""
                  className="h-9 w-9 rounded-md object-cover"
                />
                <span className="hidden md:inline text-sm font-medium max-w-[9rem] truncate">
                  {zelt.name}
                </span>
                <button
                  type="button"
                  onClick={() => onRemove(zelt.id)}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  aria-label={`${zelt.name} aus Vergleich entfernen`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {Array.from({ length: Math.max(0, 4 - zelte.length) }).map((_, i) => (
              <button
                key={`empty-${i}`}
                type="button"
                onClick={() => onNavigate("/zelte")}
                className="hidden sm:flex h-11 min-w-[11rem] items-center justify-center rounded-lg border border-dashed text-xs text-muted-foreground hover:border-primary hover:text-primary"
              >
                + Zelt hinzufügen
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              className="hidden sm:inline-flex h-9"
            >
              Leeren
            </Button>
            <Button
              size="sm"
              onClick={() => onNavigate(`/vergleich?ids=${ids.join(",")}`)}
              className="h-9 rounded-lg gap-1.5"
            >
              Vergleichen
              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 text-[10px] font-bold bg-primary-foreground/20 rounded-full">
                {ids.length}
              </span>
              <ArrowRight className="h-4 w-4 hidden sm:inline" />
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}
